// src/components/ChartComponent.js - CORRECT FIX USING CHART API

import React, { useEffect, useRef, useState } from 'react';
import {
  ChartCanvas,
  Chart,
  CandlestickSeries,
  XAxis,
  YAxis,
  CrossHairCursor,
  EdgeIndicator,
  CurrentCoordinate,
  MouseCoordinateX,
  MouseCoordinateY,
} from 'react-financial-charts';
import { format } from 'd3-format';
import { timeFormat } from 'd3-time-format';
import { discontinuousTimeScaleProvider } from "@react-financial-charts/scales";
import { Box, CircularProgress, Typography, Button, Chip } from '@mui/material';
import axios from 'axios';
import { useSelector } from 'react-redux';

const API_BASE_URL = 'https://gritwears.store/api';

const getChartColors = (isDark) => ({
  green: '#26a69a',
  red: '#ef5350',
  text: isDark ? '#e0e0e0' : '#1a1a2e',
  background: isDark ? '#121826' : '#ffffff',
  grid: isDark ? '#2a2e39' : '#e9ecef',
  crosshair: isDark ? '#758696' : '#6c757d',
});

const priceFormatter = format('.5f');
const dateFormatter = timeFormat('%H:%M:%S');

const ChartComponent = ({ symbol, interval, onPriceUpdate, category, socket }) => {
  const { user } = useSelector((state) => state.auth);
  const chartContainerRef = useRef(null);
  const chartCanvasRef = useRef(null);
  const [ohlcData, setOhlcData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [width, setWidth] = useState(800);
  const [connectionError, setConnectionError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceChange, setPriceChange] = useState(0);
  const [dataSource, setDataSource] = useState('loading');
  const [isLive, setIsLive] = useState(false);
  const [lastPriceTime, setLastPriceTime] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [chartKey, setChartKey] = useState(0); // Force re-render
  
  const currentCandleRef = useRef(null);
  const lastCandleTimeRef = useRef(null);
  const priceUpdateIntervalRef = useRef(null);
  const lastPriceRef = useRef(null);
  const mountedRef = useRef(true);
  const isSubscribedRef = useRef(false);
  const symbolRef = useRef(symbol);
  const intervalRef = useRef(interval);
  const xScaleRef = useRef(null);
  const xAccessorRef = useRef(null);
  const chartDataRef = useRef([]);
  
  const isDark = document.body.classList.contains('dark-mode');
  const colors = getChartColors(isDark);

  // Update refs when props change
  useEffect(() => {
    symbolRef.current = symbol;
    intervalRef.current = interval;
  }, [symbol, interval]);

  // Update width on resize
  useEffect(() => {
    const updateWidth = () => {
      const containerWidth = chartContainerRef.current?.offsetWidth || 800;
      setWidth(Math.min(containerWidth, 1200));
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // ============================================
  // AUTO-SCROLL USING CHART API - CORRECT FIX
  // ============================================

  // Force the chart to show the latest candle by updating xExtents
  const scrollToLatestCandle = () => {
    if (!chartCanvasRef.current || !chartDataRef.current || chartDataRef.current.length === 0) {
      return;
    }

    try {
      // Get the chart canvas instance
      const chart = chartCanvasRef.current;
      
      // Get the last candle data
      const lastDataPoint = chartDataRef.current[chartDataRef.current.length - 1];
      if (!lastDataPoint) return;

      // Use the chart's built-in method to set the visible range
      // This is the CORRECT way to control the chart view
      if (chart && typeof chart.focusRange === 'function') {
        // Focus on the last candle
        chart.focusRange(lastDataPoint);
        console.log('📊 Scrolled to latest candle using focusRange');
        return;
      }

      // Alternative: Use xExtents with the last few candles
      if (chart && typeof chart.setExtents === 'function') {
        const numCandlesToShow = Math.min(50, chartDataRef.current.length);
        const startIndex = Math.max(0, chartDataRef.current.length - numCandlesToShow);
        const startData = chartDataRef.current[startIndex];
        const endData = chartDataRef.current[chartDataRef.current.length - 1];
        
        if (startData && endData) {
          chart.setExtents([startData, endData]);
          console.log('📊 Scrolled to latest candle using setExtents');
        }
        return;
      }
    } catch (error) {
      console.error('Error scrolling chart:', error);
    }

    // Fallback: DOM scroll
    if (chartContainerRef.current) {
      const scrollContainer = chartContainerRef.current.querySelector(
        '.react-financial-charts__chart-canvas-wrapper'
      )?.parentElement;
      
      if (scrollContainer) {
        scrollContainer.scrollLeft = scrollContainer.scrollWidth;
        console.log('📊 Scrolled to latest candle using DOM fallback');
      }
    }
  };

  // Force scroll with multiple attempts
  const forceScrollToLatest = () => {
    // Attempt 1: Immediate
    setTimeout(() => scrollToLatestCandle(), 50);
    
    // Attempt 2: After 200ms
    setTimeout(() => scrollToLatestCandle(), 200);
    
    // Attempt 3: After 500ms
    setTimeout(() => scrollToLatestCandle(), 500);
    
    // Attempt 4: After 1000ms
    setTimeout(() => scrollToLatestCandle(), 1000);
  };

  // ============================================
  // SUBSCRIPTION FUNCTIONS
  // ============================================

  const subscribeToPriceUpdates = async () => {
    if (!user?.uid || !symbol || isSubscribedRef.current) return;
    
    try {
      console.log(`📡 Subscribing to ${symbol} price updates...`);
      const response = await axios.post(
        `${API_BASE_URL}/market-data/subscribe/${user.uid}/${encodeURIComponent(symbol)}`
      );
      
      if (response.data.success) {
        isSubscribedRef.current = true;
        setIsSubscribed(true);
        console.log(`✅ Subscribed to ${symbol} price updates`);
        
        if (response.data.currentPrice) {
          setCurrentPrice(response.data.currentPrice);
          lastPriceRef.current = response.data.currentPrice;
          if (onPriceUpdate) {
            onPriceUpdate(response.data.currentPrice, 0);
          }
          setLastPriceTime(new Date());
          setIsLive(true);
        }
      }
    } catch (error) {
      console.error('Failed to subscribe to price updates:', error);
    }
  };

  const unsubscribeFromPriceUpdates = async () => {
    if (!user?.uid || !symbol || !isSubscribedRef.current) return;
    
    try {
      await axios.post(
        `${API_BASE_URL}/market-data/unsubscribe/${user.uid}/${encodeURIComponent(symbol)}`
      );
      isSubscribedRef.current = false;
      setIsSubscribed(false);
      console.log(`✅ Unsubscribed from ${symbol} price updates`);
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
    }
  };

  // ============================================
  // SOCKET LISTENER FOR PRICE UPDATES
  // ============================================

  useEffect(() => {
    if (!socket) return;
    
    const handlePriceUpdate = (data) => {
      if (data.symbol === symbolRef.current && mountedRef.current) {
        const price = data.price;
        const change = data.change || 0;
        
        setCurrentPrice(price);
        setLastPriceTime(new Date(data.timestamp || Date.now()));
        
        if (lastPriceRef.current && lastPriceRef.current !== 0) {
          const finalChange = change || ((price - lastPriceRef.current) / lastPriceRef.current) * 100;
          setPriceChange(finalChange);
          if (onPriceUpdate) {
            onPriceUpdate(price, finalChange);
          }
        } else {
          if (onPriceUpdate) {
            onPriceUpdate(price, 0);
          }
        }
        lastPriceRef.current = price;
        
        if (!isLive) {
          setIsLive(true);
        }
        
        updateFormingCandle(price, Date.now());
      }
    };
    
    socket.on('price_update', handlePriceUpdate);
    
    return () => {
      socket.off('price_update', handlePriceUpdate);
    };
  }, [socket, onPriceUpdate]);

  // ============================================
  // CANDLE FORMATION
  // ============================================

  const updateFormingCandle = (price, now) => {
    const intervalSeconds = parseInt(intervalRef.current);
    const timeframeMs = intervalSeconds * 1000;
    
    if (!lastCandleTimeRef.current) {
      lastCandleTimeRef.current = now - timeframeMs;
    }
    
    const currentCandleTime = Math.floor(now / timeframeMs) * timeframeMs;
    
    if (currentCandleTime > lastCandleTimeRef.current) {
      if (currentCandleRef.current) {
        setOhlcData(prev => {
          const lastCandle = prev[prev.length - 1];
          if (lastCandle && lastCandle.date === lastCandleTimeRef.current) {
            return prev;
          }
          return [...prev, {
            ...currentCandleRef.current,
            date: lastCandleTimeRef.current
          }];
        });
      }
      
      currentCandleRef.current = {
        date: currentCandleTime,
        open: price,
        high: price,
        low: price,
        close: price,
      };
      lastCandleTimeRef.current = currentCandleTime;
      
      // Scroll on new candle
      setTimeout(() => forceScrollToLatest(), 100);
      
    } else if (currentCandleRef.current) {
      currentCandleRef.current = {
        ...currentCandleRef.current,
        high: Math.max(currentCandleRef.current.high, price),
        low: Math.min(currentCandleRef.current.low, price),
        close: price,
      };
      
      setOhlcData(prev => {
        if (prev.length === 0) return prev;
        const lastIndex = prev.length - 1;
        if (prev[lastIndex].date === currentCandleTime) {
          return [...prev.slice(0, -1), { ...currentCandleRef.current }];
        }
        return [...prev, { ...currentCandleRef.current }];
      });
    }
  };

  // ============================================
  // FETCH HISTORICAL CANDLES
  // ============================================

  const fetchHistoricalCandles = async () => {
    if (!user?.uid || !symbol) return false;
    
    setChartLoading(true);
    setConnectionError(false);
    setErrorMessage('');
    
    try {
      const limit = 300;
      console.log(`📊 Fetching candles for ${symbol} on ${interval}s timeframe...`);
      const response = await axios.get(
        `${API_BASE_URL}/market-data/candles/${user.uid}/${encodeURIComponent(symbol)}/${interval}?limit=${limit}`
      );
      
      const data = response.data;
      
      if (data.candles && data.candles.length > 0) {
        const formattedData = data.candles.map(candle => ({
          date: candle.time,
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
        }));
        
        if (mountedRef.current) {
          setOhlcData(formattedData);
          setDataSource(data.source || 'api');
          
          if (formattedData.length > 0) {
            const lastCandle = formattedData[formattedData.length - 1];
            lastCandleTimeRef.current = lastCandle.date;
            setCurrentPrice(lastCandle.close);
            lastPriceRef.current = lastCandle.close;
            
            if (onPriceUpdate) {
              onPriceUpdate(lastCandle.close, 0);
            }
            setLastPriceTime(new Date());
          }
          
          setChartLoading(false);
          setConnectionError(false);
          
          // Subscribe to real-time price updates
          await subscribeToPriceUpdates();
          
          // Start REST polling as fallback
          startRestPolling();
          
          // Force chart to re-render with new key to reset view
          setChartKey(prev => prev + 1);
          
          // Force scroll after data loads
          setTimeout(() => forceScrollToLatest(), 200);
          setTimeout(() => forceScrollToLatest(), 500);
          setTimeout(() => forceScrollToLatest(), 1000);
        }
        return true;
      } else {
        if (mountedRef.current) {
          setChartLoading(false);
          setConnectionError(true);
          setErrorMessage(data.message || 'No historical data available. Start monitoring this symbol.');
        }
        return false;
      }
    } catch (error) {
      console.error('Failed to fetch historical candles:', error);
      if (mountedRef.current) {
        setChartLoading(false);
        setConnectionError(true);
        setErrorMessage(error.response?.data?.message || 'Failed to load chart data');
      }
      return false;
    }
  };

  // ============================================
  // REST POLLING FALLBACK
  // ============================================

  const startRestPolling = () => {
    if (priceUpdateIntervalRef.current) {
      clearInterval(priceUpdateIntervalRef.current);
    }
    
    priceUpdateIntervalRef.current = setInterval(async () => {
      if (!mountedRef.current || !symbolRef.current || !user?.uid) return;
      
      try {
        const response = await axios.get(
          `${API_BASE_URL}/market-data/tick/${user.uid}/${encodeURIComponent(symbolRef.current)}`
        );
        
        if (response.data.success && response.data.price && mountedRef.current) {
          const price = response.data.price;
          setCurrentPrice(price);
          setLastPriceTime(new Date());
          
          if (lastPriceRef.current && lastPriceRef.current !== 0) {
            const change = ((price - lastPriceRef.current) / lastPriceRef.current) * 100;
            setPriceChange(change);
            if (onPriceUpdate) {
              onPriceUpdate(price, change);
            }
          } else {
            if (onPriceUpdate) {
              onPriceUpdate(price, 0);
            }
          }
          lastPriceRef.current = price;
          updateFormingCandle(price, Date.now());
          
          if (!isLive) {
            setIsLive(true);
          }
        }
      } catch (error) {
        // Silently fail
      }
    }, 2000);
  };

  // ============================================
  // MAIN EFFECT - INITIALIZE CHART
  // ============================================

  useEffect(() => {
    if (!symbol || !interval) return;

    // Clean up previous subscriptions and intervals
    const cleanup = async () => {
      if (priceUpdateIntervalRef.current) {
        clearInterval(priceUpdateIntervalRef.current);
        priceUpdateIntervalRef.current = null;
      }
      await unsubscribeFromPriceUpdates();
    };

    // Reset state for new symbol
    mountedRef.current = true;
    setChartLoading(true);
    setConnectionError(false);
    setErrorMessage('');
    setOhlcData([]);
    currentCandleRef.current = null;
    lastCandleTimeRef.current = null;
    setCurrentPrice(null);
    lastPriceRef.current = null;
    setPriceChange(0);
    setIsLive(false);
    isSubscribedRef.current = false;
    setIsSubscribed(false);
    
    // Update refs
    symbolRef.current = symbol;
    intervalRef.current = interval;
    
    // Clean up and fetch new data
    cleanup().then(() => {
      setTimeout(() => {
        if (mountedRef.current) {
          fetchHistoricalCandles();
        }
      }, 100);
    });

    return () => {
      mountedRef.current = false;
      if (priceUpdateIntervalRef.current) {
        clearInterval(priceUpdateIntervalRef.current);
      }
      unsubscribeFromPriceUpdates();
    };
  }, [symbol, interval, user?.uid]);

  // ============================================
  // STORE CHART DATA REF FOR SCROLLING
  // ============================================

  // This effect runs whenever ohlcData changes and stores it in ref
  useEffect(() => {
    if (ohlcData.length > 0) {
      // Process data with xScaleProvider to get the final chart data
      try {
        const xScaleProvider = discontinuousTimeScaleProvider.inputDateAccessor(d => new Date(d.date));
        const { data: processedData, xScale, xAccessor, displayXAccessor } = xScaleProvider(ohlcData);
        chartDataRef.current = processedData;
        xScaleRef.current = xScale;
        xAccessorRef.current = xAccessor;
      } catch (error) {
        // If processing fails, use raw data
        chartDataRef.current = ohlcData;
      }
    }
  }, [ohlcData]);

  // ============================================
  // HANDLE REFRESH
  // ============================================

  const handleRefresh = () => {
    fetchHistoricalCandles();
  };

  // ============================================
  // RENDER STATES
  // ============================================

  if (chartLoading) {
    return (
      <Box sx={{ 
        height: 500, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: colors.background,
        borderRadius: '12px',
        border: `1px solid ${colors.grid}`,
      }}>
        <CircularProgress sx={{ color: colors.green }} />
        <Typography sx={{ mt: 2, color: colors.text }}>
          Loading chart data...
        </Typography>
        <Typography variant="caption" sx={{ mt: 1, color: colors.text, opacity: 0.6 }}>
          {symbol} - {interval}s timeframe
          {category && ` • ${category}`}
        </Typography>
        {isSubscribed && (
          <Typography variant="caption" sx={{ mt: 1, color: colors.green, opacity: 0.8 }}>
            ✅ Real-time updates active
          </Typography>
        )}
      </Box>
    );
  }

  if (connectionError || !ohlcData || ohlcData.length === 0) {
    return (
      <Box sx={{ 
        height: 500, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: colors.background,
        borderRadius: '12px',
        border: `1px solid ${colors.grid}`,
        p: 3,
      }}>
        <Typography sx={{ color: colors.text, fontSize: '18px', mb: 2 }}>
          📊 No chart data available
        </Typography>
        <Typography variant="caption" sx={{ color: colors.text, textAlign: 'center', maxWidth: '80%', mb: 2 }}>
          {errorMessage || `Start monitoring ${symbol} to generate chart data.`}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.text, textAlign: 'center', maxWidth: '80%', mb: 2, opacity: 0.6 }}>
          💡 Tip: Go to the left panel, select this symbol and timeframe, then click "Start Monitoring"
        </Typography>
        <Button 
          variant="contained" 
          onClick={handleRefresh}
          sx={{ 
            backgroundColor: colors.green,
            '&:hover': { backgroundColor: '#1d8a7e' }
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // ============================================
  // RENDER CHART
  // ============================================

  try {
    const validData = ohlcData.filter(d => 
      d && typeof d.date === 'number' && 
      !isNaN(d.open) && !isNaN(d.high) && 
      !isNaN(d.low) && !isNaN(d.close)
    );
    
    if (validData.length === 0) {
      return (
        <Box sx={{ 
          height: 500, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: colors.background,
          borderRadius: '12px',
        }}>
          <Typography sx={{ color: colors.text }}>
            No valid chart data available
          </Typography>
        </Box>
      );
    }
    
    const xScaleProvider = discontinuousTimeScaleProvider.inputDateAccessor(d => new Date(d.date));
    const { data: chartData, xScale, xAccessor, displayXAccessor } = xScaleProvider(validData);
    const start = xAccessor(chartData[0]);
    const end = xAccessor(chartData[chartData.length - 1]);

    const candleWidth = 6;
    const chartWidth = Math.max(width - 100, chartData.length * candleWidth);

    return (
      <Box>
        {/* Status bar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5, 
          mb: 1.5,
          px: 2,
          py: 0.75,
          backgroundColor: isDark ? 'rgba(38, 166, 154, 0.1)' : 'rgba(38, 166, 154, 0.05)',
          borderRadius: '8px',
          flexWrap: 'wrap',
          border: `1px solid ${isLive ? colors.green + '40' : colors.grid}`,
          transition: 'all 0.3s ease',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: isLive ? colors.green : colors.grid,
              animation: isLive ? 'pulse 1s ease-in-out infinite' : 'none',
              boxShadow: isLive ? `0 0 12px ${colors.green}60` : 'none',
            }} />
            <Typography variant="caption" sx={{ 
              color: isLive ? colors.green : colors.text, 
              fontWeight: 600,
              fontSize: '11px',
            }}>
              {isLive ? 'LIVE' : 'HISTORICAL'}
            </Typography>
          </Box>
          
          {isSubscribed && (
            <Chip 
              label="Subscribed" 
              size="small" 
              sx={{ 
                backgroundColor: colors.green + '20',
                color: colors.green,
                height: '20px',
                '& .MuiChip-label': { fontSize: '10px', px: 1 }
              }} 
            />
          )}
          
          {category && (
            <Chip 
              label={category} 
              size="small" 
              sx={{ 
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                color: colors.text,
                height: '20px',
                '& .MuiChip-label': { fontSize: '10px', px: 1 }
              }} 
            />
          )}
          
          <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5, fontSize: '10px' }}>
            {dataSource === 'active-trader' ? '🔵 Live Monitor' : 
             dataSource === 'provider' ? '📊 Market Data' : 
             dataSource === 'api' ? '📡 API' : 'WebSocket'}
          </Typography>
          
          {lastPriceTime && (
            <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5, fontSize: '10px', ml: 'auto' }}>
              Updated: {lastPriceTime.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
      
        <Box 
          ref={chartContainerRef}
          sx={{ 
            width: '100%', 
            overflowX: 'auto',
            overflowY: 'hidden',
            backgroundColor: colors.background,
            borderRadius: '12px',
            border: `1px solid ${colors.grid}`,
            position: 'relative',
            '&::-webkit-scrollbar': {
              height: '6px',
              backgroundColor: colors.grid,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: colors.green,
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#1d8a7e',
              },
            },
          }}
          id="chart-scroll-container"
        >
          <Box sx={{ 
            minWidth: chartWidth,
            position: 'relative',
            height: 500,
          }}>
            <ChartCanvas
              ref={chartCanvasRef}
              key={chartKey}
              height={500}
              width={chartWidth}
              ratio={1}
              margin={{ left: 50, right: 50, top: 10, bottom: 30 }}
              data={chartData}
              xScale={xScale}
              xAccessor={xAccessor}
              displayXAccessor={displayXAccessor}
              seriesName="Candles"
              xExtents={[start, end]}
              padding={{ left: 20, right: 20 }}
              panEvent={true}
              zoomEvent={true}
            >
              <Chart 
                id={1} 
                yExtents={d => [d.high, d.low]}
                padding={{ top: 10, bottom: 20 }}
              >
                <XAxis 
                  stroke={colors.grid}
                  tickStroke={colors.grid}
                  tickLabelFill={colors.text}
                />
                <YAxis 
                  stroke={colors.grid}
                  tickStroke={colors.grid}
                  tickLabelFill={colors.text}
                  ticks={10}
                />
                
                <CandlestickSeries
                  wickStroke={d => d.close > d.open ? colors.green : colors.red}
                  fill={d => d.close > d.open ? colors.green : colors.red}
                  stroke={d => d.close > d.open ? colors.green : colors.red}
                />
                
                {currentPrice && (
                  <CurrentCoordinate
                    yAccessor={() => currentPrice}
                    fill={colors.text}
                    stroke={colors.crosshair}
                    strokeDasharray="ShortDash"
                  />
                )}
                
                <MouseCoordinateX
                  displayFormat={dateFormatter}
                  fill={colors.background}
                  textFill={colors.text}
                />
                <MouseCoordinateY
                  displayFormat={priceFormatter}
                  fill={colors.background}
                  textFill={colors.text}
                />
                
                <EdgeIndicator
                  itemType="last"
                  orient="right"
                  displayFormat={priceFormatter}
                  fill={d => d.close > d.open ? colors.green : colors.red}
                  lineStroke={d => d.close > d.open ? colors.green : colors.red}
                  textFill={colors.text}
                  yAccessor={d => d.close}
                />
              </Chart>
              <CrossHairCursor 
                stroke={colors.crosshair}
                strokeDasharray="ShortDash"
              />
            </ChartCanvas>
          </Box>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mt: 1,
          px: 1,
        }}>
          <Typography variant="caption" sx={{ color: colors.text, opacity: 0.4, fontSize: '10px' }}>
            {isLive ? '🟢 Live data from backend' : '🟡 Historical data only'}
            {isSubscribed && ' • Subscribed to updates'}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.text, opacity: 0.4, fontSize: '10px' }}>
            {validData.length} candles • {symbol} • {interval}s
          </Typography>
        </Box>
      </Box>
    );
  } catch (error) {
    console.error('Chart rendering error:', error);
    return (
      <Box sx={{ 
        height: 500, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: colors.background,
        borderRadius: '12px',
        flexDirection: 'column',
        gap: 2,
      }}>
        <Typography sx={{ color: colors.text }}>
          Error rendering chart: {error.message}
        </Typography>
        <Button 
          variant="contained" 
          onClick={handleRefresh}
          sx={{ 
            backgroundColor: colors.green,
            '&:hover': { backgroundColor: '#1d8a7e' }
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }
};

// Add pulse animation
const styles = `
  @keyframes pulse {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(0.8); }
    100% { opacity: 1; transform: scale(1); }
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

export default ChartComponent;